import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { GoogleMap, Marker, Polyline } from '@react-google-maps/api';
import axios from 'axios';
import { saveAs } from 'file-saver';
import API_BASE_URL from '../config/api';
import { DEFAULT_LOCATION } from '../constants/placeTypes';

// Promenadfilm: a walk between two map points replayed as Street View stills.
// Route: Directions API (legacy, still enabled on the key) in WALKING mode.
// Frames: route sampled every `spacing` m, each sample snapped to the nearest
// official panorama with StreetViewService (metadata, free), stills fetched via
// the backend /streetview proxy - data URLs, so the canvas stays untainted and
// can be recorded to a video file with MediaRecorder.
// Must render inside StreetAnalyzer, which loads the Maps script (geometry lib).

const mapContainerStyle = { width: '100%', height: '420px' };
// Hide Google's place icons - they compete with the route and the A/B markers.
const MAP_OPTIONS = {
  streetViewControl: false,
  mapTypeControl: false,
  clickableIcons: false,
  styles: [
    { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  ],
};
const FRAME_W = 640;
const FRAME_H = 360; // Street View Static max width is 640; 16:9 for video
const FRAME_FOV = 90;
const MAX_SAMPLES = 400; // panorama lookups per walk (metadata, free)
const MAX_FRAMES = 200; // stills per walk - every one is a billed Street View Static request
const LOOKAHEAD_M = 20; // view direction = route tangent over the next 20 m
const SNAP_CONCURRENCY = 8;
const FETCH_CONCURRENCY = 6;
const SPACING_OPTIONS = [5, 10, 15, 25, 50];
const DEFAULT_FPS = 3; // default play time of a new walk = frames / 3 s
const MAX_FPS = 30; // = the canvas capture rate; faster would drop frames from the video
const MAX_PLAY_S = 600;
const FADE_SHARE = 0.4; // share of each frame's time spent fading into the next

const cssToken = (name) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();

const formatDistance = (m) => (m < 1000
  ? `${Math.round(m)} m`
  : `${(m / 1000).toLocaleString('sv-SE', { maximumFractionDigits: 2 })} km`);

const spherical = () => window.google.maps.geometry.spherical;

// Cumulative distance (m) at each vertex of a google.maps.LatLng path.
const measurePath = (path) => {
  const cum = [0];
  for (let i = 1; i < path.length; i++) {
    cum.push(cum[i - 1] + spherical().computeDistanceBetween(path[i - 1], path[i]));
  }
  return cum;
};

const pointAt = (path, cum, d) => {
  const total = cum[cum.length - 1];
  if (d <= 0) return path[0];
  if (d >= total) return path[path.length - 1];
  let lo = 0;
  let hi = cum.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (cum[mid] <= d) lo = mid; else hi = mid;
  }
  const seg = cum[hi] - cum[lo];
  return seg > 0 ? spherical().interpolate(path[lo], path[hi], (d - cum[lo]) / seg) : path[lo];
};

// Walking direction at distance d, 0–360.
const headingAt = (path, cum, d) => {
  const total = cum[cum.length - 1];
  const from = Math.max(0, Math.min(d, total - LOOKAHEAD_M));
  const h = spherical().computeHeading(pointAt(path, cum, from), pointAt(path, cum, Math.min(total, from + LOOKAHEAD_M)));
  return (h + 360) % 360;
};

// Distance along the path of the path point closest to p, searched in [from, to].
// Local equirectangular projection - fine at street scale.
const projectOnPath = (path, cum, p, from, to) => {
  const kx = 111320 * Math.cos((p.lat() * Math.PI) / 180);
  const ky = 110540;
  const px = p.lng() * kx;
  const py = p.lat() * ky;
  let best = { d: Math.max(0, (from + to) / 2), dist2: Infinity };
  for (let i = 1; i < path.length; i++) {
    if (cum[i] < from || cum[i - 1] > to) continue;
    const ax = path[i - 1].lng() * kx;
    const ay = path[i - 1].lat() * ky;
    const dx = path[i].lng() * kx - ax;
    const dy = path[i].lat() * ky - ay;
    const len2 = dx * dx + dy * dy;
    const t = len2 > 0 ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2)) : 0;
    const qx = ax + t * dx - px;
    const qy = ay + t * dy - py;
    const dist2 = qx * qx + qy * qy;
    if (dist2 < best.dist2) best = { d: cum[i - 1] + t * (cum[i] - cum[i - 1]), dist2 };
  }
  return best.d;
};

const sampleDistances = (total, spacing) => {
  const out = [];
  for (let d = 0; d < total; d += spacing) out.push(d);
  out.push(total);
  return out;
};

const routeWalk = async (origin, destination) => {
  const { DirectionsService, TravelMode } = window.google.maps;
  let res;
  try {
    res = await new DirectionsService().route({ origin, destination, travelMode: TravelMode.WALKING });
  } catch (err) {
    throw new Error(err?.code === 'ZERO_RESULTS'
      ? 'Ingen gångväg hittades mellan punkterna.'
      : `Gångvägen kunde inte hämtas (${err?.code || err?.message}).`);
  }
  const legs = res.routes[0].legs;
  // Step paths are full resolution; overview_path is simplified.
  const path = [];
  legs.forEach(leg => leg.steps.forEach(step => step.path.forEach(p => {
    if (!path.length || !path[path.length - 1].equals(p)) path.push(p);
  })));
  const cum = measurePath(path);
  return { path, cum, total: cum[cum.length - 1], duration: legs.reduce((s, l) => s + l.duration.value, 0) };
};

const fitRoute = (map, route) => {
  if (!map || !route) return;
  const bounds = new window.google.maps.LatLngBounds();
  route.path.forEach(p => bounds.extend(p));
  map.fitBounds(bounds, 48);
};

const findPanorama = async (service, location, radius) => {
  const { StreetViewPreference, StreetViewSource } = window.google.maps;
  const request = { location, radius, preference: StreetViewPreference.NEAREST };
  // Official Google imagery only - user photospheres (indoors, odd angles) break the walk.
  if (StreetViewSource.GOOGLE) request.sources = [StreetViewSource.GOOGLE];
  else request.source = StreetViewSource.OUTDOOR;
  try {
    const { data } = await service.getPanorama(request);
    return data?.location?.pano ? data : null;
  } catch {
    return null; // ZERO_RESULTS: no panorama within radius
  }
};

// Unique panoramas in walking order. Dense coverage and crossings can snap a
// sample to a panorama behind the previous one - dropped, so the film never
// steps backwards.
const pickStops = (panos, samples, { path, cum }, spacing) => {
  const stops = [];
  const seen = new Set();
  panos.forEach((data, i) => {
    if (!data || seen.has(data.location.pano)) return;
    const ll = data.location.latLng;
    const d = projectOnPath(path, cum, ll, samples[i] - 2 * spacing, samples[i] + 2 * spacing);
    if (stops.length && d <= stops[stops.length - 1].d) return;
    seen.add(data.location.pano);
    stops.push({
      pano: data.location.pano,
      lat: ll.lat(),
      lng: ll.lng(),
      d,
      heading: headingAt(path, cum, d),
      date: data.imageDate || '',
    });
  });
  return stops;
};

// At most `max` items, evenly spread, first and last kept.
const thinEvenly = (list, max) => (list.length <= max
  ? list
  : Array.from({ length: max }, (_, k) => list[Math.round((k * (list.length - 1)) / (max - 1))]));

// Run fn over items with at most `limit` in flight; results keep input order.
const mapLimit = async (items, limit, fn, onProgress, run) => {
  const results = new Array(items.length).fill(null);
  let next = 0;
  let done = 0;
  const worker = async () => {
    while (next < items.length && !run.cancelled) {
      const i = next++;
      results[i] = await fn(items[i]);
      onProgress(++done);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
};

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error('Bilden kunde inte läsas'));
  img.src = src;
});

const pickMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return null;
  // MP4 first: plays in PowerPoint/QuickTime. Safari and recent Chrome record it.
  return ['video/mp4;codecs=avc1.42E01E', 'video/mp4;codecs=avc1', 'video/mp4', 'video/webm;codecs=vp9', 'video/webm']
    .find(t => MediaRecorder.isTypeSupported(t)) || null;
};

const StreetWalk = ({ active }) => {
  const [points, setPoints] = useState({ a: null, b: null });
  const [route, setRoute] = useState(null); // {path, cum, total, duration}
  const [spacing, setSpacing] = useState(15);
  const [progress, setProgress] = useState(null); // {label, done, total}
  const [walk, setWalk] = useState(null); // {frames, total}
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [playTime, setPlayTime] = useState(''); // film length in seconds, as typed
  const [fade, setFade] = useState(true);
  const [recording, setRecording] = useState(false);
  const [status, setStatus] = useState('Klicka på kartan där promenaden ska börja (A).');

  const mapRef = useRef(null);
  const canvasRef = useRef(null);
  const runRef = useRef(null); // {cancelled} of the frame generation in flight
  const posRef = useRef(0); // playback position in frames (float)
  const recorderRef = useRef(null);
  const discardRef = useRef(false);
  const onEndRef = useRef(null);
  const mountedRef = useRef(true);

  const colors = useMemo(() => ({
    teal: cssToken('--teal'),
    tealDeep: cssToken('--teal-deep'),
    clay: cssToken('--clay'),
  }), []);

  const busy = !!progress || recording;
  const frames = walk?.frames || [];
  const estimate = route ? sampleDistances(route.total, spacing).length : 0;
  const tooLong = estimate > MAX_SAMPLES;

  // Play time drives the frame rate: every frame gets playSeconds / n.
  const minPlayTime = Math.max(1, Math.ceil(frames.length / MAX_FPS));
  const typedPlayTime = parseFloat(String(playTime).replace(',', '.'));
  const playSeconds = Math.min(MAX_PLAY_S, Math.max(minPlayTime, typedPlayTime > 0
    ? typedPlayTime
    : Math.round(frames.length / DEFAULT_FPS)));
  const fps = frames.length ? frames.length / playSeconds : DEFAULT_FPS;

  const stopRecording = useCallback((discard) => {
    if (recorderRef.current?.state === 'recording') {
      discardRef.current = discard;
      recorderRef.current.stop();
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (runRef.current) runRef.current.cancelled = true;
      stopRecording(true);
    };
  }, [stopRecording]);

  // Hidden behind another tab: stop playback (and a recording in progress).
  useEffect(() => {
    if (!active) {
      setPlaying(false);
      stopRecording(true);
    }
  }, [active, stopRecording]);

  // New start/end: drop the old walk and fetch the walking route.
  useEffect(() => {
    if (runRef.current) runRef.current.cancelled = true;
    runRef.current = null;
    setProgress(null);
    setWalk(null);
    setPlaying(false);
    setRoute(null);
    if (!points.a || !points.b) {
      setStatus(points.a
        ? 'Klicka på kartan där promenaden ska sluta (B).'
        : 'Klicka på kartan där promenaden ska börja (A).');
      return undefined;
    }
    let stale = false;
    setStatus('Hämtar gångväg...');
    routeWalk(points.a, points.b)
      .then((r) => {
        if (stale) return;
        if (r.total < 5) {
          setStatus('Fel: A och B ligger för nära varandra.');
          return;
        }
        setRoute(r);
        fitRoute(mapRef.current, r);
        setStatus(`Gångväg ${formatDistance(r.total)}, ca ${Math.max(1, Math.round(r.duration / 60))} min. Välj bildavstånd och klicka "Skapa promenad".`);
      })
      .catch((err) => { if (!stale) setStatus(`Fel: ${err.message}`); });
    return () => { stale = true; };
  }, [points]);

  // The film column narrows the map: fit the route again once the layout has settled.
  useEffect(() => {
    if (!walk) return undefined;
    const t = setTimeout(() => fitRoute(mapRef.current, route), 150);
    return () => clearTimeout(t);
  }, [walk, route]);

  const handleMapClick = useCallback((e) => {
    const p = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    setPoints(prev => ((!prev.a || prev.b) ? { a: p, b: null } : { a: prev.a, b: p }));
  }, []);

  const handleDragEnd = (key) => (e) => {
    const p = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    setPoints(prev => ({ ...prev, [key]: p }));
  };

  const generate = async () => {
    if (!route || tooLong) return;
    const run = { cancelled: false };
    runRef.current = run;
    setWalk(null);
    setPlaying(false);
    const { path, cum, total } = route;
    const samples = sampleDistances(total, spacing);
    const service = new window.google.maps.StreetViewService();
    const radius = Math.max(10, Math.min(25, spacing));
    const onProgress = (done) => { if (!run.cancelled) setProgress(p => p && { ...p, done }); };

    setStatus('Skapar promenad...');
    setProgress({ label: 'Söker Street View-panoramor längs vägen', done: 0, total: samples.length });
    const panos = await mapLimit(samples, SNAP_CONCURRENCY,
      d => findPanorama(service, pointAt(path, cum, d), radius), onProgress, run);
    if (run.cancelled) return;

    const found = pickStops(panos, samples, route, spacing);
    const stops = thinEvenly(found, MAX_FRAMES);
    if (!stops.length) {
      runRef.current = null;
      setProgress(null);
      setStatus('Fel: Det finns inga Street View-bilder längs den här gångvägen.');
      return;
    }

    let lastError = null;
    setProgress({ label: 'Hämtar Street View-bilder', done: 0, total: stops.length });
    const loaded = await mapLimit(stops, FETCH_CONCURRENCY, async (stop) => {
      // One retry on network errors and 5xx (cold starts, throttling); a 4xx is final.
      for (let attempt = 0; ; attempt++) {
        try {
          const { data } = await axios.post(`${API_BASE_URL}/streetview`, {
            pano: stop.pano,
            lat: stop.lat,
            lng: stop.lng,
            heading: Math.round(stop.heading),
            pitch: 0,
            fov: FRAME_FOV,
            size: `${FRAME_W}x${FRAME_H}`,
          });
          return data.image ? { ...stop, image: await loadImage(data.image) } : null;
        } catch (err) {
          const code = err.response?.status;
          if (attempt === 0 && !(code >= 400 && code < 500)) continue;
          lastError = err.response?.data?.error || err.message;
          return null;
        }
      }
    }, onProgress, run);
    if (run.cancelled) return;

    runRef.current = null;
    setProgress(null);
    const result = loaded.filter(Boolean);
    if (!result.length) {
      setStatus(`Fel: Inga bilder kunde hämtas${lastError ? ` (${lastError})` : ''}.`);
      return;
    }
    posRef.current = 0;
    setCurrent(0);
    setWalk({ frames: result, total });
    setPlayTime(t => t || String(Math.max(1, Math.round(result.length / DEFAULT_FPS))));
    const notes = [];
    if (found.length > stops.length) notes.push(`glesat från ${found.length} till ${stops.length} – max ${MAX_FRAMES} per film`);
    if (stops.length > result.length) notes.push(`${stops.length - result.length} kunde inte hämtas`);
    setStatus(`${result.length} bilder längs ${formatDistance(total)}${notes.length ? ` (${notes.join('; ')})` : ''}. Klicka på Spela.`);
  };

  const cancelGenerate = () => {
    if (runRef.current) runRef.current.cancelled = true;
    runRef.current = null;
    setProgress(null);
    setStatus('Hämtningen avbröts.');
  };

  const draw = useCallback((pos) => {
    const canvas = canvasRef.current;
    if (!canvas || !walk) return;
    const { frames: list, total } = walk;
    const ctx = canvas.getContext('2d');
    const i = Math.max(0, Math.min(Math.floor(pos), list.length - 1));
    const t = pos - i;
    ctx.globalAlpha = 1;
    ctx.drawImage(list[i].image, 0, 0, FRAME_W, FRAME_H);
    if (fade && i + 1 < list.length && t > 1 - FADE_SHARE) {
      ctx.globalAlpha = (t - (1 - FADE_SHARE)) / FADE_SHARE;
      ctx.drawImage(list[i + 1].image, 0, 0, FRAME_W, FRAME_H);
    }

    // Caption strip, burned into the recorded video too (incl. the © Google credit).
    const h = 30;
    ctx.globalAlpha = 0.75;
    ctx.fillStyle = colors.tealDeep;
    ctx.fillRect(0, FRAME_H - h, FRAME_W, h);
    ctx.globalAlpha = 1;
    ctx.fillStyle = colors.clay;
    ctx.fillRect(0, FRAME_H - 3, FRAME_W * Math.min(1, list[i].d / total), 3);
    ctx.fillStyle = '#fff';
    ctx.font = '600 13px Inter, -apple-system, "Segoe UI", sans-serif';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    ctx.fillText(`${formatDistance(list[i].d)} av ${formatDistance(total)}`, 12, FRAME_H - h / 2 - 1);
    ctx.textAlign = 'right';
    ctx.fillText(`Bild © Google${list[i].date ? ` · ${list[i].date}` : ''}`, FRAME_W - 12, FRAME_H - h / 2 - 1);
  }, [walk, fade, colors]);

  useEffect(() => {
    if (!playing) draw(posRef.current);
  }, [draw, playing]);

  // Playback: frame i is on screen during pos ∈ [i, i+1).
  useEffect(() => {
    if (!playing || !walk) return undefined;
    const n = walk.frames.length;
    let raf;
    let last = performance.now();
    const tick = (now) => {
      const dt = Math.min(0.5, Math.max(0, now - last) / 1000);
      last = now;
      const pos = posRef.current + dt * fps;
      if (pos >= n) {
        posRef.current = n - 1;
        draw(n - 1);
        setCurrent(n - 1);
        setPlaying(false);
        onEndRef.current?.();
        return;
      }
      posRef.current = pos;
      draw(pos);
      setCurrent(Math.floor(pos));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, fps, walk, draw]);

  const togglePlay = () => {
    if (!playing && posRef.current >= frames.length - 1) {
      posRef.current = 0;
      setCurrent(0);
    }
    setPlaying(p => !p);
  };

  const scrubTo = (i) => {
    setPlaying(false);
    posRef.current = i;
    setCurrent(i);
    draw(i);
  };

  // Records one real-time playback from the start; the tab must stay visible
  // (requestAnimationFrame pauses in background tabs).
  const recordVideo = async () => {
    const canvas = canvasRef.current;
    const mimeType = pickMimeType();
    if (!canvas?.captureStream || !mimeType) {
      setStatus('Fel: Webbläsaren kan inte spela in video. Prova Chrome, Edge, Firefox eller Safari.');
      return;
    }
    const stream = canvas.captureStream(30);
    const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 6000000 });
    const chunks = [];
    recorder.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
    const stopped = new Promise((resolve) => { recorder.onstop = resolve; });
    recorderRef.current = recorder;
    discardRef.current = false;
    onEndRef.current = () => stopRecording(false);

    setRecording(true);
    setStatus('Spelar in video – håll fliken synlig tills inspelningen är klar.');
    posRef.current = 0;
    setCurrent(0);
    draw(0);
    recorder.start(1000);
    setPlaying(true);

    await stopped;
    stream.getTracks().forEach(t => t.stop());
    onEndRef.current = null;
    recorderRef.current = null;
    if (!mountedRef.current) return;
    setRecording(false);
    setPlaying(false);
    if (discardRef.current) {
      setStatus('Inspelningen avbröts.');
      return;
    }
    const ext = mimeType.startsWith('video/mp4') ? 'mp4' : 'webm';
    saveAs(new Blob(chunks, { type: mimeType.split(';')[0] }), `promenad_${new Date().toISOString().slice(0, 10)}.${ext}`);
    setStatus(`Videon är sparad som ${ext.toUpperCase()}.`);
  };

  const polylineOptions = useMemo(() => ({
    strokeColor: colors.teal, strokeOpacity: 0.85, strokeWeight: 5, clickable: false,
  }), [colors]);

  const frame = frames[current];

  return (
    <div className={`walkfilm-section${walk ? ' has-film' : ''}`}>
      <div className="walkfilm-setup">
        <h3>1. Välj start (A) och mål (B)</h3>
        <p className="walkfilm-hint">
          Klicka på kartan där promenaden börjar och sedan där den slutar. Dra i markörerna för att justera.
        </p>
        <div className="walkfilm-map">
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={DEFAULT_LOCATION}
            zoom={15}
            onLoad={(m) => { mapRef.current = m; }}
            onClick={busy ? undefined : handleMapClick}
            options={MAP_OPTIONS}
          >
            {route && <Polyline path={route.path} options={polylineOptions} />}
            {points.a && (
              <Marker position={points.a} label={{ text: 'A', color: '#fff', fontWeight: '600' }}
                draggable={!busy} onDragEnd={handleDragEnd('a')} />
            )}
            {points.b && (
              <Marker position={points.b} label={{ text: 'B', color: '#fff', fontWeight: '600' }}
                draggable={!busy} onDragEnd={handleDragEnd('b')} />
            )}
            {frame && (
              <Marker
                position={{ lat: frame.lat, lng: frame.lng }}
                clickable={false}
                zIndex={10}
                icon={{
                  path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                  rotation: frame.heading,
                  scale: 6,
                  fillColor: colors.clay,
                  fillOpacity: 1,
                  strokeColor: '#fff',
                  strokeWeight: 2,
                }}
              />
            )}
          </GoogleMap>
        </div>

        <div className="walkfilm-toolbar">
          {route && (
            <span className="walkfilm-route-info">
              {formatDistance(route.total)} · ca {Math.max(1, Math.round(route.duration / 60))} min
            </span>
          )}
          <label data-tip="street.walk.spacing">
            Bild var
            <select value={spacing} onChange={(e) => setSpacing(Number(e.target.value))} disabled={busy}>
              {SPACING_OPTIONS.map(s => <option key={s} value={s}>{s} m</option>)}
            </select>
          </label>
          {route && (
            <span className={`walkfilm-estimate${tooLong ? ' too-long' : ''}`}>
              {tooLong
                ? `För lång sträcka för ${spacing} m – välj större avstånd`
                : `upp till ${Math.min(estimate, MAX_FRAMES)} bilder`}
            </span>
          )}
          <button onClick={generate} disabled={!route || busy || tooLong} data-tip="street.walk.generate">
            Skapa promenad
          </button>
          <button className="walkfilm-secondary" onClick={() => setPoints({ a: null, b: null })}
            disabled={busy || !points.a} data-tip="street.walk.clear">
            Rensa
          </button>
        </div>

        {progress && (
          <div className="walkfilm-progress">
            <div className="walkfilm-progress-label">{progress.label} – {progress.done} av {progress.total}</div>
            <div className="walkfilm-progress-bar">
              <span style={{ width: `${(progress.done / Math.max(1, progress.total)) * 100}%` }} />
            </div>
            <div>
              <button className="walkfilm-secondary" onClick={cancelGenerate} data-tip="street.walk.cancel">Avbryt</button>
            </div>
          </div>
        )}
      </div>

      {walk && (
        <div className="walkfilm-player">
          <h3>2. Promenaden</h3>
          <canvas ref={canvasRef} width={FRAME_W} height={FRAME_H} className="walkfilm-canvas" />
          <div className="walkfilm-controls">
            <button onClick={togglePlay} disabled={recording} data-tip="street.walk.play">
              {playing ? 'Paus' : 'Spela'}
            </button>
            <input
              type="range"
              className="walkfilm-scrub"
              min={0}
              max={frames.length - 1}
              step={1}
              value={current}
              onChange={(e) => scrubTo(Number(e.target.value))}
              disabled={recording}
              data-tip="street.walk.scrub"
            />
            <span className="walkfilm-counter">{current + 1} / {frames.length}</span>
          </div>
          <div className="walkfilm-controls">
            <label data-tip="street.walk.playTime">
              Speltid
              <input
                type="number"
                className="walkfilm-seconds"
                min={minPlayTime}
                max={MAX_PLAY_S}
                step={1}
                value={playTime}
                onChange={(e) => setPlayTime(e.target.value)}
                onBlur={() => setPlayTime(String(playSeconds))}
                disabled={recording}
              />
              s
            </label>
            <label className="walkfilm-check" data-tip="street.walk.fade">
              <input type="checkbox" checked={fade} onChange={(e) => setFade(e.target.checked)} disabled={recording} />
              Mjuka övergångar
            </label>
            {recording ? (
              <button className="walkfilm-secondary" onClick={() => stopRecording(true)} data-tip="street.walk.stopRecording">
                Avbryt inspelning
              </button>
            ) : (
              <button onClick={recordVideo} data-tip="street.walk.download">Ladda ner video</button>
            )}
          </div>
          <p className="walkfilm-note">
            Filmen blir {playSeconds.toLocaleString('sv-SE', { maximumFractionDigits: 1 })} s
            ({fps.toLocaleString('sv-SE', { maximumFractionDigits: 1 })} bilder/s). Videon spelas in i realtid.
          </p>
        </div>
      )}

      <div className={`walkfilm-status ${status.includes('Fel') ? 'error' : 'success'}`}>
        {status}
      </div>
    </div>
  );
};

export default StreetWalk;
